// Pure model behind the overlay create/edit form + its live preview (docs/09 §C2).
// Kept dependency-free and framework-free so the "preview state machine" and the
// "at least one of logo/ticker" invariant are unit-testable without React.

import {
  OVERLAY_OPACITY_MAX,
  OVERLAY_OPACITY_MIN,
  OVERLAY_SIZE_PCT_MAX,
  OVERLAY_SIZE_PCT_MIN,
  type OverlayPosition,
} from '@seencast/shared';

export interface OverlayFormValues {
  name: string;
  logo_media_id: string | null;
  position: OverlayPosition;
  size_pct: number;
  opacity: number;
  ticker_text: string;
}

export interface OverlayPreviewModel {
  hasLogo: boolean;
  hasTicker: boolean;
  /** At least one of logo/ticker — the create/edit invariant. */
  hasContent: boolean;
  /** A named overlay WITH content is savable. */
  isValid: boolean;
  /** Why it is not valid (for inline hints); null when valid. */
  error: string | null;
  /** CSS corner class for the preview logo. */
  cornerClass: string;
  /** Clamped logo width (% of preview width). */
  sizePct: number;
  /** Logo opacity as a 0..1 fraction. */
  opacity: number;
  /** Trimmed ticker text (empty string when none). */
  tickerText: string;
}

export function clampSize(pct: number): number {
  if (!Number.isFinite(pct)) return OVERLAY_SIZE_PCT_MIN;
  return Math.min(OVERLAY_SIZE_PCT_MAX, Math.max(OVERLAY_SIZE_PCT_MIN, Math.round(pct)));
}

export function clampOpacity(pct: number): number {
  if (!Number.isFinite(pct)) return OVERLAY_OPACITY_MIN;
  return Math.min(OVERLAY_OPACITY_MAX, Math.max(OVERLAY_OPACITY_MIN, Math.round(pct)));
}

export function overlayPreviewModel(v: OverlayFormValues): OverlayPreviewModel {
  const hasLogo = !!v.logo_media_id;
  const tickerText = v.ticker_text.trim();
  const hasTicker = tickerText.length > 0;
  const hasContent = hasLogo || hasTicker;
  const namedOk = v.name.trim().length > 0;
  const error = !namedOk
    ? 'Give the overlay a name.'
    : !hasContent
      ? 'Add a logo or ticker text — an overlay needs at least one.'
      : null;
  return {
    hasLogo,
    hasTicker,
    hasContent,
    isValid: error === null,
    error,
    cornerClass: `pos-${v.position}`,
    sizePct: clampSize(v.size_pct),
    opacity: clampOpacity(v.opacity) / 100,
    tickerText,
  };
}
